import React, { useState, useMemo, useEffect, useRef } from "react";
import "react-calendar/dist/Calendar.css";
import Calendar from "react-calendar";
import "./Events.css";
import { Modal, SearchSelect } from "@beleef/ui";
import Button from "components/ui/Button";
import { AiOutlineCalendar, AiOutlineClockCircle } from "react-icons/ai";
import { BiMap, BiHomeAlt } from "react-icons/bi";
import { FaEdit, FaTrash } from "react-icons/fa";
import { format, addMinutes } from "date-fns";
import { useForm, Controller } from "react-hook-form";
import { showToast } from "components/ui/toast";
import { isEqual } from "lodash";
import { useGetProductsQuery } from "apis/products";
import { useCartDetailQuery, useCartPackagesQuery } from "apis/campaignFlow";
import {
  useEventsQuery,
  useEventPropertiesQuery,
  useGetEventsByPropertyId,
  useAddEventMutation,
  useUpdateEventMutation,
  useDeleteEventMutation,
} from "apis/event";
import { useGetContractorQuery } from "apis/contractor";
import {
  SERVICE_CONFIG,
  buildMediaGroups,
  contractorsForGroup,
  groupFromEvent,
  groupToServices,
  groupToSummary,
  serviceFingerprint,
} from "utils/event";
import { CustomCalendar } from "@beleef/calendar";
import EventLogs from "components/events/EventLogs";

// ─── Helpers ───
const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString(undefined, {
    weekday: "long",
    year: "numeric",
    month: "long",
    day: "numeric",
  });

const formatTime = (dateString) =>
  new Date(dateString).toLocaleTimeString(undefined, {
    hour: "2-digit",
    minute: "2-digit",
  });

const tileDisabled = ({ date, view }) => {
  if (view !== "month") return false;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const compareDate = new Date(date);
  compareDate.setHours(0, 0, 0, 0);
  return compareDate < today;
};

const buildStartDate = (selectedDate, timeValue) => {
  const [h, m] = timeValue.split(":").map(Number);
  const d = new Date(selectedDate);
  d.setHours(h, m, 0, 0);
  return d;
};

// ─── EndTimeField Component ───
// Editable end time — defaults to start + service durations, but agents can
// take more or less time per property, so allow a manual override.
const EndTimeField = ({ selectedDate, selectedTime, endTime, onChange }) => {
  const endTimeSlots = useMemo(() => {
    if (!selectedTime?.value) return [];
    const start = buildStartDate(selectedDate, selectedTime.value);
    const slots = [];
    const current = new Date(start);
    current.setMinutes(current.getMinutes() + 15);
    while (current.getDate() === start.getDate()) {
      slots.push({
        value: format(current, "HH:mm"),
        label: format(current, "hh:mm a"),
      });
      current.setMinutes(current.getMinutes() + 15);
    }
    return slots;
  }, [selectedDate, selectedTime]);

  const durationMinutes =
    endTime && selectedTime?.value
      ? Math.round(
          (endTime - buildStartDate(selectedDate, selectedTime.value)) / 60000,
        )
      : null;

  return (
    <div>
      <label className="form-label">End Time *</label>
      <SearchSelect
        options={endTimeSlots}
        value={
          endTime
            ? {
                value: format(endTime, "HH:mm"),
                label: format(endTime, "hh:mm a"),
              }
            : null
        }
        onChange={(opt) => {
          if (!opt?.value) return;
          onChange(buildStartDate(selectedDate, opt.value));
        }}
        searchable
        placeholder="Select end time"
      />
      {durationMinutes > 0 && (
        <p className="text-xs text-darkergray mt-1">
          Duration: {durationMinutes} min
        </p>
      )}
    </div>
  );
};

// Error-border helper (SearchSelect has no `styles` prop like react-select)
const errorBorderClass = (fieldError) => (fieldError ? "border-red-500" : "");

// ─── Bookable services for a property ───
// Built from the property's cart — the same source an agent booking uses —
// rather than the whole product catalogue. Post-production, print and portal
// items are excluded in buildMediaGroups: they're purchased work with no
// on-site attendance, and booking one here would send the contractor a
// calendar invite for a job nobody attends.
const usePropertyMediaGroups = (propertyId) => {
  const { data: products, isPending: productsPending } = useGetProductsQuery({
    showAll: "true",
  });
  const cartQuery = useCartDetailQuery(propertyId);
  const packagesQuery = useCartPackagesQuery(propertyId);
  const eventsQuery = useGetEventsByPropertyId(propertyId);

  const groups = useMemo(
    () =>
      buildMediaGroups({
        existingEvents: eventsQuery.data || [],
        products: products || [],
        carts: cartQuery.data?.carts || [],
        packages: packagesQuery.data || [],
      }),
    [eventsQuery.data, products, cartQuery.data, packagesQuery.data],
  );

  const isLoading =
    !!propertyId &&
    (productsPending ||
      cartQuery.isLoading ||
      packagesQuery.isLoading ||
      eventsQuery.isLoading);

  return { groups, isLoading };
};

// ─── MediaGroupPicker Component ───
// One row per contractor service + company — that pairing IS the job, since a
// contractor pool is scoped to the company that sold the product. Everything
// in a row is booked together as a single event, so the backend's
// "one service type per event" rule can't be broken from here.
const MediaGroupPicker = ({
  groups,
  isLoading,
  value,
  onChange,
  error,
  propertyId,
}) => (
  <div>
    <label className="form-label">Select Services *</label>

    {!propertyId ? (
      <p className="text-sm text-darkergray">Choose an address first.</p>
    ) : isLoading ? (
      <p className="text-sm text-darkergray">Loading services…</p>
    ) : groups.length === 0 ? (
      <p className="text-sm text-darkergray">
        Nothing to book for this property — its marketing has no photography,
        video or floorplan items.
      </p>
    ) : (
      <div className="space-y-3">
        {groups.map((group) => {
          const config = SERVICE_CONFIG[group.contractorService] || {
            dot: "bg-darkgray",
            label: group.contractorService || "Service",
          };
          const isSelected = value?.groupKey === group.groupKey;
          return (
            <div key={group.groupKey}>
              <div className="flex items-center gap-2 mb-1.5">
                <div className={`w-2 h-2 rounded-full ${config.dot}`} />
                <span className="text-sm font-semibold">{config.label}</span>
                {group.contractorCompanyName && (
                  <span className="text-xs text-darkergray">
                    {group.contractorCompanyName}
                  </span>
                )}
              </div>
              <div
                onClick={() => onChange(isSelected ? null : group)}
                className={`ml-4 flex items-center justify-between px-3 py-2 border rounded-lg cursor-pointer transition-colors ${
                  isSelected
                    ? "border-black bg-lightergray"
                    : "border-mediumgray hover:border-darkgray"
                }`}
              >
                <p className="text-sm font-medium">{group.summary}</p>
                {group.isScheduled ? (
                  <span className="text-xs text-blue-600 ml-2">
                    Already booked
                  </span>
                ) : group.totalDuration ? (
                  <span className="text-xs text-darkergray ml-2">
                    {group.totalDuration} min
                  </span>
                ) : null}
              </div>
            </div>
          );
        })}
      </div>
    )}

    {error && <p className="form-error-message">{error}</p>}
  </div>
);

// ─── AddEvent Component ───
const AddEvent = ({ contractors, properties, setAddEvent }) => {
  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm();
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [endTime, setEndTime] = useState(null);
  const addEventMutation = useAddEventMutation();
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupError, setGroupError] = useState(null);

  const selectedProperty = watch("property");
  const propertyId = selectedProperty?.value || null;
  const { groups: allGroups, isLoading: groupsLoading } =
    usePropertyMediaGroups(propertyId);

  // Locked work is already done and can't be moved — it isn't bookable.
  const groups = useMemo(
    () => allGroups.filter((group) => !group.isLocked),
    [allGroups],
  );

  const selectedTime = watch("time");

  // A different property has a different cart, so the chosen job no longer
  // applies. Same for the contractor, whose pool is scoped to the group.
  useEffect(() => {
    setSelectedGroup(null);
  }, [propertyId]);

  useEffect(() => {
    setValue("contractor", null);
  }, [selectedGroup, setValue]);

  useEffect(() => {
    if (selectedTime?.value && selectedGroup) {
      const start = buildStartDate(selectedDate, selectedTime.value);
      setEndTime(addMinutes(start, selectedGroup.totalDuration || 60));
    } else {
      setEndTime(null);
    }
  }, [selectedGroup, selectedTime, selectedDate]);

  const timeSlots = useMemo(() => {
    const slots = [];
    const current = new Date(selectedDate);
    current.setHours(0, 0, 0, 0);
    while (current.getDate() === selectedDate.getDate()) {
      slots.push({
        value: format(current, "HH:mm"),
        label: format(current, "hh:mm a"),
      });
      current.setMinutes(current.getMinutes() + 15);
    }
    return slots;
  }, [selectedDate]);

  const propertyOptions = useMemo(
    () => properties.map((p) => ({ label: p.address, value: p.propertyId })),
    [properties],
  );
  const contractorOptions = useMemo(
    () =>
      contractorsForGroup(contractors, selectedGroup).map((c) => ({
        label: c.name,
        value: c._id,
      })),
    [contractors, selectedGroup],
  );

  const onSubmit = async (formData) => {
    if (!selectedGroup) {
      setGroupError("Select the work being booked");
      return;
    }
    try {
      const [hours, minutes] = formData.time.value.split(":").map(Number);
      const startDate = new Date(selectedDate);
      startDate.setHours(hours, minutes, 0, 0);
      if (!startDate || !endTime)
        throw new Error("Invalid date/time selection");

      await addEventMutation.mutateAsync({
        contractorId: formData.contractor.value,
        propertyId: selectedProperty.value,
        summary: groupToSummary(selectedGroup),
        address: selectedProperty.label,
        start: startDate.toISOString(),
        end: endTime.toISOString(),
        additionalNotes: formData.additionalNotes || "",
        notify: !formData.notifyNoOne,
        services: groupToServices(selectedGroup),
        eventTypeKey: null,
        campaignEventId: null,
      });

      showToast("success", "Event added successfully");
      setAddEvent(false);
    } catch (error) {
      console.error("Error submitting event:", error);
      showToast(
        "error",
        error?.response?.data?.message || error.message || "Failed to add event",
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      {/* Address first — the bookable services come from this property's cart */}
      <div>
        <label className="form-label">Select Address *</label>
        <Controller
          control={control}
          name="property"
          rules={{ required: "Address is required" }}
          render={({ field }) => (
            <SearchSelect
              options={propertyOptions}
              value={field.value ?? null}
              onChange={field.onChange}
              searchable
              isClearable
              placeholder="Select address"
              className={errorBorderClass(errors.property)}
            />
          )}
        />
        {errors.property && (
          <p className="form-error-message">{errors.property.message}</p>
        )}
      </div>

      <MediaGroupPicker
        groups={groups}
        isLoading={groupsLoading}
        propertyId={propertyId}
        value={selectedGroup}
        onChange={(group) => {
          setSelectedGroup(group);
          setGroupError(null);
        }}
        error={groupError}
      />

      <div className="w-full flex items-center justify-center">
        <Calendar
          tileDisabled={tileDisabled}
          prev2Label={null}
          next2Label={null}
          value={selectedDate}
          onChange={(date) => {
            setSelectedDate(date);
            setValue("date", date);
          }}
        />
      </div>

      <div>
        <label className="form-label">Select Time Slot *</label>
        <Controller
          control={control}
          name="time"
          rules={{ required: "Time slot is required" }}
          render={({ field }) => (
            <SearchSelect
              options={timeSlots}
              value={field.value ?? null}
              onChange={field.onChange}
              searchable
              placeholder="Select a time"
              className={errorBorderClass(errors.time)}
            />
          )}
        />
        {errors.time && (
          <p className="form-error-message">{errors.time.message}</p>
        )}
      </div>

      {endTime && selectedTime && (
        <EndTimeField
          selectedDate={selectedDate}
          selectedTime={selectedTime}
          endTime={endTime}
          onChange={setEndTime}
        />
      )}

      <div>
        <label className="form-label">Select Contractor *</label>
        <Controller
          control={control}
          name="contractor"
          rules={{ required: "Contractor is required" }}
          render={({ field }) => (
            <SearchSelect
              options={contractorOptions}
              value={field.value ?? null}
              onChange={field.onChange}
              searchable
              placeholder="Choose a contractor..."
              className={errorBorderClass(errors.contractor)}
            />
          )}
        />
        {errors.contractor && (
          <p className="form-error-message">{errors.contractor.message}</p>
        )}
        {selectedGroup && contractorOptions.length === 0 && (
          <p className="text-xs text-darkergray mt-1">
            No-one at{" "}
            {selectedGroup.contractorCompanyName || "the supplying company"}{" "}
            offers this work.
          </p>
        )}
      </div>

      <div>
        <label className="form-label">Additional Notes</label>
        <textarea
          {...register("additionalNotes")}
          className="form-textarea border border-mediumgray"
          rows={3}
          placeholder="Add notes here"
        />
      </div>

      <div className="flex items-center">
        <label className="form-label">
          Notify No-one
          <input type="checkbox" className="ml-2" {...register("notifyNoOne")} />
        </label>
      </div>

      <div className="flex justify-end mt-4">
        <Button
          type="submit"
          loading={addEventMutation.isPending}
          disabled={addEventMutation.isPending}
          className="black-button"
        >
          Create
        </Button>
      </div>
    </form>
  );
};

// ─── EditEvent Component ───
const EditEvent = ({ contractors, eventData, setIsEditing }) => {
  const updateEventMutation = useUpdateEventMutation();
  const isTentative = eventData?.type === "tentative";

  const { groups: cartGroups, isLoading: groupsLoading } =
    usePropertyMediaGroups(eventData.propertyId);

  // The event's own services[], turned back into a group. Covers bookings the
  // cart no longer explains — a free shoot, a since-removed product, anything
  // booked before carts. Without it the field would open empty, and saving
  // would then wipe services[] off the event.
  const eventGroup = useMemo(() => groupFromEvent(eventData), [eventData]);

  // The group this event was booked from, matched on the campaign events it
  // covers. Falls back to the event's own services when the cart has moved on.
  const initialGroup = useMemo(() => {
    const fingerprint = serviceFingerprint(eventData.services);
    const matched = fingerprint
      ? cartGroups.find(
          (group) =>
            serviceFingerprint(groupToServices(group)) === fingerprint,
        )
      : null;
    return matched || eventGroup;
  }, [cartGroups, eventData.services, eventGroup]);

  // Offer the cart's groups, plus the event's own when it isn't one of them.
  // Locked work stays hidden unless it's what this event is — an event being
  // edited must always show what it was booked for.
  const groups = useMemo(() => {
    const offered = cartGroups.filter(
      (group) => !group.isLocked || group.groupKey === initialGroup?.groupKey,
    );
    if (!initialGroup?.isFromEvent) return offered;
    return [initialGroup, ...offered];
  }, [cartGroups, initialGroup]);

  const [selectedGroup, setSelectedGroup] = useState(null);
  const [groupError, setGroupError] = useState(null);

  // The group can only be resolved once the cart and products have loaded, so
  // it can't be a useForm default — those are read once, on the first render,
  // and never re-applied.
  const groupInitialisedRef = useRef(false);
  useEffect(() => {
    if (groupInitialisedRef.current || groupsLoading) return;
    groupInitialisedRef.current = true;
    setSelectedGroup(initialGroup);
  }, [groupsLoading, initialGroup]);

  const contractorOptions = useMemo(
    () =>
      contractorsForGroup(contractors, selectedGroup).map((c) => ({
        label: c.name,
        value: c._id,
      })),
    [contractors, selectedGroup],
  );

  const {
    register,
    handleSubmit,
    control,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    defaultValues: {
      ...eventData,
      property: { label: eventData.address, value: eventData.propertyId },
      time: {
        value: format(new Date(eventData.start), "HH:mm"),
        label: format(new Date(eventData.start), "hh:mm a"),
      },
      contractor: eventData?.contractorId?._id
        ? {
            label: eventData.contractorId.name,
            value: eventData.contractorId._id,
          }
        : null,
    },
  });

  const [selectedDate, setSelectedDate] = useState(new Date(eventData.start));
  const [endTime, setEndTime] = useState(new Date(eventData.end));
  const [isChanged, setIsChanged] = useState(false);

  const selectedTime = watch("time");
  const watchedValues = watch();

  // Tracks the (date, start, services) combo the end time was last auto-set
  // for. Seeded with the event's initial values so the stored end (possibly
  // manually adjusted earlier) isn't overwritten on open — tentative events
  // keep the old always-recompute behaviour.
  const endAutoKeyRef = useRef(
    isTentative
      ? null
      : [
          format(new Date(eventData.start), "yyyy-MM-dd"),
          format(new Date(eventData.start), "HH:mm"),
          serviceFingerprint(eventData.services),
        ].join("|"),
  );

  useEffect(() => {
    const buildStart = (date, time) => {
      if (!date || !time) return null;
      const [h, m] = time.value.split(":").map(Number);
      const d = new Date(date);
      d.setHours(h, m, 0, 0);
      return d.toISOString();
    };

    const current = {
      contractorId: watchedValues.contractor?.value || null,
      serviceIds: serviceFingerprint(groupToServices(selectedGroup)),
      start: buildStart(selectedDate, watchedValues.time),
      end: isTentative ? null : endTime ? endTime.toISOString() : null,
      additionalNotes: watchedValues.additionalNotes || "",
      notifyNoOne: Boolean(watchedValues.notifyNoOne),
    };

    const original = {
      contractorId: eventData?.contractorId?._id || null,
      serviceIds: serviceFingerprint(eventData.services),
      start: new Date(eventData.start).toISOString(),
      end: isTentative ? null : new Date(eventData.end).toISOString(),
      additionalNotes: eventData.additionalNotes || "",
      // Not saved on the event, so an opened event starts unticked.
      notifyNoOne: false,
    };

    setIsChanged(!isEqual(current, original));
  }, [
    watchedValues,
    selectedDate,
    endTime,
    eventData,
    selectedGroup,
    isTentative,
  ]);

  useEffect(() => {
    if (!selectedTime?.value || !selectedGroup) return;
    const key = [
      format(selectedDate, "yyyy-MM-dd"),
      selectedTime.value,
      serviceFingerprint(groupToServices(selectedGroup)),
    ].join("|");
    if (endAutoKeyRef.current === key) return;
    endAutoKeyRef.current = key;
    const start = buildStartDate(selectedDate, selectedTime.value);
    setEndTime(addMinutes(start, selectedGroup.totalDuration || 60));
  }, [selectedGroup, selectedTime, selectedDate]);

  const timeSlots = useMemo(() => {
    const slots = [];
    const current = new Date(selectedDate);
    current.setHours(8, 0, 0, 0);
    const end = new Date(selectedDate);
    end.setHours(20, 0, 0, 0);
    while (current < end) {
      slots.push({
        value: format(current, "HH:mm"),
        label: format(current, "hh:mm a"),
      });
      current.setMinutes(current.getMinutes() + 15);
    }
    return slots;
  }, [selectedDate]);

  const onSubmit = async (formData) => {
    if (!isChanged) return;
    if (!selectedGroup) {
      setGroupError("Select the work being booked");
      return;
    }
    try {
      const [h, m] = formData.time.value.split(":").map(Number);
      const startDate = new Date(selectedDate);
      startDate.setHours(h, m, 0, 0);
      if (!startDate || !endTime)
        throw new Error("Invalid date/time selection");

      await updateEventMutation.mutateAsync({
        _id: eventData._id,
        contractorId: formData.contractor?.value || null,
        propertyId: eventData.propertyId,
        // A group rebuilt from the event keeps the title it was saved with —
        // its items are service keys, not names.
        summary: selectedGroup.isFromEvent
          ? eventData.summary
          : groupToSummary(selectedGroup),
        address: eventData.address,
        start: startDate.toISOString(),
        end: endTime.toISOString(),
        additionalNotes: formData.additionalNotes || "",
        notify: !formData.notifyNoOne,
        services: groupToServices(selectedGroup),
        eventTypeKey: null,
        campaignEventId: null,
      });

      showToast("success", "Event updated successfully");
      setIsEditing(false);
    } catch (error) {
      console.error("Error submitting event:", error);
      showToast(
        "error",
        error?.response?.data?.message ||
          error.message ||
          "Failed to update event",
      );
    }
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
      <div className="w-full flex items-center justify-center">
        <Calendar
          prev2Label={null}
          next2Label={null}
          value={selectedDate}
          onChange={(date) => {
            setSelectedDate(date);
            setValue("date", date);
          }}
        />
      </div>

      <div>
        <label className="form-label">Select Time Slot *</label>
        <Controller
          control={control}
          name="time"
          rules={{ required: "Time slot is required" }}
          render={({ field }) => (
            <SearchSelect
              options={timeSlots}
              value={field.value ?? null}
              onChange={field.onChange}
              searchable
              placeholder="Select a time"
              className={errorBorderClass(errors.time)}
            />
          )}
        />
        {errors.time && (
          <p className="form-error-message">{errors.time.message}</p>
        )}
      </div>

      <div>
        <label className="form-label">Selected Address</label>
        <p>{eventData.address}</p>
      </div>

      <MediaGroupPicker
        groups={groups}
        isLoading={groupsLoading}
        propertyId={eventData.propertyId}
        value={selectedGroup}
        onChange={(group) => {
          setSelectedGroup(group);
          setGroupError(null);
        }}
        error={groupError}
      />

      {isTentative
        ? endTime && (
            <p>
              <strong>End Time:</strong> {format(endTime, "hh:mm a")}
              {selectedGroup && (
                <span className="text-darkergray text-sm ml-2">
                  ({selectedGroup.totalDuration} min total)
                </span>
              )}
            </p>
          )
        : endTime &&
          selectedTime && (
            <EndTimeField
              selectedDate={selectedDate}
              selectedTime={selectedTime}
              endTime={endTime}
              onChange={setEndTime}
            />
          )}

      <div>
        <label className="form-label">Select Contractor *</label>
        <Controller
          control={control}
          name="contractor"
          rules={{ required: "Contractor is required" }}
          render={({ field }) => (
            <SearchSelect
              options={contractorOptions}
              value={field.value ?? null}
              onChange={field.onChange}
              searchable
              isClearable
              placeholder="Choose a contractor..."
              className={errorBorderClass(errors.contractor)}
            />
          )}
        />
        {errors.contractor && (
          <p className="form-error-message">{errors.contractor.message}</p>
        )}
      </div>

      <div>
        <label className="form-label">Additional Notes</label>
        <textarea
          {...register("additionalNotes")}
          className="form-textarea border border-mediumgray"
          rows={3}
          placeholder="Add notes here"
        />
      </div>

      <div className="flex items-center">
        <label className="form-label">
          Notify No-one
          <input type="checkbox" className="ml-2" {...register("notifyNoOne")} />
        </label>
      </div>

      <div className="flex justify-end mt-4">
        <Button
          type="submit"
          className="black-button"
          loading={updateEventMutation.isPending}
          disabled={!isChanged || updateEventMutation.isPending}
        >
          Save
        </Button>
      </div>
    </form>
  );
};

// ─── Invite Status ───
// The contractor's own answer to the calendar invite, straight off the
// organiser calendar. A declined booking still looks scheduled everywhere
// else, so it has to be called out here — this modal is where it gets
// rescheduled from. Nothing is shown for an accepted invite, or when there's
// no invite to answer.
const INVITE_STATUS_LABEL = {
  declined: ["Contractor declined this invite", "text-red-600"],
  needsAction: ["Awaiting contractor response", "text-amber-600"],
  tentative: ["Contractor marked this maybe", "text-amber-600"],
};

const InviteStatus = ({ status }) => {
  const label = INVITE_STATUS_LABEL[status];
  if (!label) return null;
  const [text, color] = label;
  return <p className={`text-sm font-semibold ${color}`}>{text}</p>;
};

// ─── EventCard Component ───
const EventCard = ({ event, contractors, shortView }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isDelete, setIsDelete] = useState(false);
  const deleteEventMutation = useDeleteEventMutation();

  const handleDeleteEvent = async () => {
    try {
      await deleteEventMutation.mutateAsync(event._id);
      showToast("success", "Event deleted successfully");
      setIsDelete(false);
    } catch (error) {
      console.error("Error deleting event:", error);
      showToast("error", error.message);
    }
  };

  return (
    <div className="text-darkergray space-y-1">
      <h6>{event.summary}</h6>

      <InviteStatus status={event.inviteStatus} />

      {event.address && (
        <div className="flex items-center space-x-2">
          <BiMap size={16} />
          <span>{event.address}</span>
        </div>
      )}

      <div className="flex items-center space-x-2">
        <AiOutlineCalendar size={16} />
        <span>{formatDate(event.start)}</span>
      </div>
      <div className="flex items-center space-x-2">
        <AiOutlineClockCircle size={16} />
        <span>
          {formatTime(event.start)} - {formatTime(event.end)}
        </span>
      </div>

      {!shortView && (
        <>
          <div className="flex items-center space-x-2">
            <BiHomeAlt size={16} />
            <span>Access: {event.access}</span>
          </div>

          <div className="border-t py-4 flex flex-col gap-4">
            {event?.contractorId && (
              <div className="w-full px-4 py-2 bg-lightgray">
                <p className="font-semibold">Contractor</p>
                <div className="flex flex-col items-start mt-2">
                  <span>
                    <strong>Name:</strong> {event.contractorId?.name}
                  </span>
                  <span>
                    <strong>Email:</strong> {event.contractorId?.email}
                  </span>
                  <span>
                    <strong>Mobile:</strong> {event.contractorId?.mobile}
                  </span>
                </div>
              </div>
            )}
            {event?.agentId && (
              <div className="w-full px-4 py-2 bg-lightgray">
                <p className="font-semibold">Agent</p>
                <div className="flex flex-col items-start mt-2">
                  <span>
                    <strong>Name:</strong> {event.agentId?.name}
                  </span>
                  <span>
                    <strong>Email:</strong> {event.agentId?.email}
                  </span>
                  <span>
                    <strong>Mobile:</strong> {event.agentId?.mobile}
                  </span>
                </div>
              </div>
            )}
          </div>

          {event.additionalNotes && (
            <div className="border-t py-4">
              <h6>Additional Notes</h6>
              <p>{event.additionalNotes}</p>
            </div>
          )}

          <div className="w-full border-t pt-4 flex items-center justify-evenly space-x-4">
            <Button
              onClick={() => setIsEditing(true)}
              className="flex items-center space-x-1 text-darkergray"
            >
              <FaEdit size={14} />
              <span className="text-sm">Edit</span>
            </Button>
            <div className="border-l border-mediumgray h-5" />
            <Button
              className="flex items-center space-x-1 text-darkergray"
              onClick={() => setIsDelete(true)}
              disabled={deleteEventMutation.isPending}
            >
              <FaTrash size={14} />
              <span className="text-sm">Delete</span>
            </Button>
          </div>

          <Modal
            isOpen={isEditing}
            onClose={() => setIsEditing(false)}
            title="Edit Event"
            className="max-w-lg min-h-[400px] max-h-[94vh]"
          >
            <EditEvent
              contractors={contractors}
              eventData={event}
              setIsEditing={setIsEditing}
            />
          </Modal>

          <Modal
            isOpen={isDelete}
            onClose={() => setIsDelete(false)}
            title="Delete Event"
          >
            <div className="flex flex-col items-center justify-center">
              <p>Are you sure you want to delete this event?</p>
              <div className="w-full flex gap-2 justify-end py-4">
                <Button
                  onClick={() => setIsDelete(false)}
                  className="gray-button"
                >
                  No
                </Button>
                <Button
                  onClick={handleDeleteEvent}
                  className="black-button"
                  loading={deleteEventMutation.isPending}
                  disabled={deleteEventMutation.isPending}
                >
                  Yes
                </Button>
              </div>
            </div>
          </Modal>
        </>
      )}
    </div>
  );
};

// ════════════════════════════════════════════════
// MAIN EVENTS PAGE
// ════════════════════════════════════════════════
const Events = () => {
  const { data: events = [] } = useEventsQuery();
  // No service filter here — each booking narrows the list to the contractors
  // who offer that job's service, at the company that sold it.
  const { data: contractors = [] } = useGetContractorQuery({
    profileComplete: true,
    isDeleted: false,
  });
  const { data: properties = [] } = useEventPropertiesQuery();

  const [selectedEventId, setSelectedEventId] = useState(null);
  const [addEvent, setAddEvent] = useState(false);

  const selectedEvent = useMemo(
    () =>
      selectedEventId
        ? events.find((e) => e._id === selectedEventId) || null
        : null,
    [selectedEventId, events],
  );

  return (
    <div
      style={{ height: "calc(100vh - 80px)" }}
      className="px-4 flex flex-col overflow-hidden"
    >
      <div className="flex-1 min-h-0 pb-4">
        <CustomCalendar
          events={events}
          onSelectEvent={(event) => setSelectedEventId(event._id)}
          onAddEvent={() => setAddEvent(true)}
          contractors={contractors}
          properties={properties}
          renderLogs={() => <EventLogs properties={properties} />}
        />
      </div>

      <Modal
        isOpen={!!selectedEvent}
        onClose={() => setSelectedEventId(null)}
        title="Event Details"
      >
        {selectedEvent && (
          <EventCard
            event={selectedEvent}
            contractors={contractors}
            shortView={selectedEvent.summary === "Personal Event"}
          />
        )}
      </Modal>

      <Modal
        isOpen={addEvent}
        onClose={() => setAddEvent(false)}
        title="Add Event"
        className="max-w-lg min-h-[400px] max-h-[94vh]"
      >
        <AddEvent
          contractors={contractors}
          properties={properties}
          setAddEvent={setAddEvent}
        />
      </Modal>
    </div>
  );
};

export default Events;
